import { getAuthElements, getApiHost } from './utils';

import type { RedirectReponse } from './interfaces/RedirectResponse';
import { redirectToLogin } from './utils/redirectToLogin';
import { getCustomerInfo } from './utils/getCustomerInfo';
import { bindAccountElements } from './utils/bindAccountElements';
import { bindLoginElements } from './utils/bindLoginElements';
import { bindRegisterElements } from './utils/bindRegisterElements';
import { bindLogoutElements } from './utils/bindLogoutElements';
import { bindPasswordElements } from './utils/bindPasswordElements';
import { Deferred } from './utils/Deferred';

const redirectConfig = new Deferred<RedirectReponse>();

function bindUiElements(storeHash: string) {
  /****************************************
   * fetch redirect configuration
   ****************************************/
  const url = `${getApiHost()}/api/redirects/${storeHash}`;
  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'any',
    },
  };
  fetch(url, options)
    .then((response) => {
      return response.json();
    })
    .then((json) => {
      redirectConfig.resolve(json);
      return json;
    })
    .catch((err) => {
      redirectConfig.reject(err);
      throw err;
    });

  /****************************************
   * bind buttons on initial content load
   ****************************************/
  document.addEventListener('DOMContentLoaded', async () => {
    const {
      $accountElements,
      $loginElements,
      $logoutElements,
      $registerElements,
      $resetPasswordElements,
    } = getAuthElements();

    document.addEventListener(
      'oidc-connect-login',
      async () => {
        const login = (await redirectConfig.promise).login;
        redirectToLogin(login);
      },
      false,
    );

    document.addEventListener(
      'oidc-connect-logout',
      async () => {
        const logout = (await redirectConfig.promise).logout;
        window.location.href = logout;
      },
      false,
    );

    // listen to button interactions
    $accountElements.forEach(bindAccountElements(redirectConfig.promise));
    $loginElements.forEach(bindLoginElements(redirectConfig.promise));
    $registerElements.forEach(bindRegisterElements(redirectConfig.promise));
    $logoutElements.forEach(bindLogoutElements(redirectConfig.promise));
    $resetPasswordElements.forEach(
      bindPasswordElements(redirectConfig.promise),
    );
  });

  /*************************************************
   * observe DOM mutations for added login buttons
   *************************************************/
  const onBindElementsAddedByJS = bindLoginElements(redirectConfig.promise);
  const bindElementsAddedByJS: MutationCallback = (records, observer) => {
    for (const record of records) {
      for (let i = 0; i < record.addedNodes.length; i++) {
        const addedNode = record.addedNodes[i];
        const loginButtons = addedNode.parentNode.querySelectorAll(
          'a[href="/login.php"]',
        );
        loginButtons.forEach(onBindElementsAddedByJS);
      }

      if (record.target.childNodes.length === 0) {
        observer.disconnect();
      }
    }
  };

  const observer = new MutationObserver(bindElementsAddedByJS);
  const container = document.querySelector('.body');
  if (container) {
    observer.observe(container, {
      childList: true,
      subtree: true,
    });
  } else {
    console.warn('Unable to bind to element `.body`');
  }
}

// @ts-expect-error - untyped property of window
window.OidcConnector = {
  redirectConfig,
  bindUiElements,
  getCustomerInfo,
  clientId: null,
};

// fire an event notifying readiness
const event = new Event('oidc-connect-ready');
document.dispatchEvent(event);
